/**
 * @description Component-DepositModalFooter
 */
import './index.less';

import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BALANCE_TYPE, ZERO } from '@/constants';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { depositOrWithdrawBalance } from '@/features/balance/actions';
import { useGateAccountState } from '@/features/balance/hook';
import { usePortfolioOperateToken, useUserWithdrawPendingToken } from '@/features/portfolio/hook';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useDappChainConfig, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { ApproveWrapButton } from '@/pages/components/WalletStatus/ApproveButton';
import { TokenInfo } from '@/types/token';
import { ISimulation } from '@/types/trade';
import { toBN, toWad } from '@/utils/numberUtil';
import { isWrappedNativeToken } from '@/utils/token';
interface IPropTypes {
  type: BALANCE_TYPE;
  quote?: TokenInfo;
  onCloseModal?: () => void;
  simulation?: ISimulation;
}
const DepositModalFooter: FC<IPropTypes> = function ({ type, quote, onCloseModal, simulation }) {
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const tradeAccountState = useGateAccountState(chainId);
  const userAddr = useUserAddr();
  const signer = useWalletSigner();
  const provider = useAppProvider();
  const { t } = useTranslation();
  const sdk = useSDK(chainId);
  const notification = useTxNotification();
  const tokenInfo = usePortfolioOperateToken(chainId, userAddr);
  const { pendingToken } = useUserWithdrawPendingToken(chainId, userAddr, quote?.address);
  const dappChainConfig = useDappChainConfig(chainId);
  const isWrappedNativeTokenInfo = useMemo(() => {
    return chainId && quote && isWrappedNativeToken(chainId, quote.address);
  }, [chainId, quote]);

  const adjustQuote = useMemo(() => {
    if (isWrappedNativeTokenInfo) {
      return tokenInfo;
    }
    return quote;
  }, [isWrappedNativeTokenInfo, quote, tokenInfo]);

  const maxWithdrawableBN = useMemo(() => {
    return WrappedBigNumber.from(pendingToken?.maxWithdrawable || 0);
  }, [pendingToken?.maxWithdrawable]);

  const isShowPendingWarning = useMemo(() => {
    if (type === BALANCE_TYPE.WITHDRAW) {
      if (pendingToken?.thresholdBalance?.eq(0)) return false;
      return pendingToken?.pendingBalance?.amount?.gt(0);
    }
    return false;
  }, [pendingToken?.pendingBalance?.amount, pendingToken?.thresholdBalance, type]);

  const isShowWithDrawLimitWarning = useMemo(() => {
    if (type === BALANCE_TYPE.WITHDRAW) {
      if (isShowPendingWarning) return false;
      if (pendingToken?.thresholdBalance?.eq(0)) return false;

      const amount = WrappedBigNumber.from(tradeAccountState?.depositAmountStr || 0);
      return amount.gt(0) && amount.gt(maxWithdrawableBN);
    }
    return false;
  }, [
    type,
    isShowPendingWarning,
    pendingToken?.thresholdBalance,
    tradeAccountState?.depositAmountStr,
    maxWithdrawableBN,
  ]);

  const onAfterClickDeposit = useCallback(async () => {
    try {
      if (
        chainId &&
        userAddr &&
        adjustQuote &&
        signer &&
        tradeAccountState?.depositAmountStr &&
        !toBN(tradeAccountState?.depositAmountStr).eq(0) &&
        sdk
      ) {
        const isDisableNotification = isShowPendingWarning || isShowWithDrawLimitWarning;

        const receipt = await dispatch(
          depositOrWithdrawBalance({
            signer,
            provider,
            sdkContext: sdk,
            quote: adjustQuote,
            amount: tradeAccountState?.depositAmountStr,
            chainId,
            account: userAddr,

            balanceType: type,
            isDisableNotification,

            sendTxNotification: notification.open,
          }),
        ).unwrap();
        if (receipt) {
          if (type === BALANCE_TYPE.WITHDRAW) {
            if (receipt?.status === 1 && isDisableNotification)
              notification.success({
                tx: receipt.transactionHash,
                message: t('notification.withdraw.success'),
                description: t('notification.withdraw.successWithLimit', {
                  quantity: WrappedBigNumber.from(tradeAccountState?.depositAmountStr).formatDisplayNumber(),
                  quote: adjustQuote.symbol,
                }),
              });
          }
          onCloseModal && onCloseModal();
        }
      }
    } catch (e) {
      console.error('🚀 ~ file: index.tsx ~ line 91 ~ onAfterClickDeposit ~ e', e);
    }
  }, [
    chainId,
    userAddr,
    adjustQuote,
    signer,
    tradeAccountState?.depositAmountStr,
    sdk,
    isShowPendingWarning,
    isShowWithDrawLimitWarning,
    dispatch,
    provider,
    type,
    onCloseModal,
    notification,
    t,
  ]);

  return (
    <div className="syn-deposit-modal-footer">
      <ApproveWrapButton
        block
        amount={type === BALANCE_TYPE.DEPOSIT ? toWad(tradeAccountState.depositAmountStr || 0) : ZERO}
        marginToken={adjustQuote}
        type="primary"
        loading={tradeAccountState.isOperating || false}
        disabled={!!simulation?.message}
        spenderAddress={dappChainConfig?.contracts?.gate || sdk?.perp?.contracts?.gate?.address}
        afterApproved={onAfterClickDeposit}>
        {t('common.confirm')}
      </ApproveWrapButton>
    </div>
  );
};

export default React.memo(DepositModalFooter);
